/**
 * Positions page with comprehensive filtering, viewing, and client-side pagination.
 *
 * Fetches all positions for the account, applies filters, then paginates the
 * filtered results client-side. This ensures filters work across the full dataset.
 */

import { useState, useMemo, useCallback } from 'react';
import { MainLayout } from '@/components/layout';
import { Card, CardHeader, CardTitle, CardContent, Button, Spinner, Pagination } from '@/components/ui';
import { AccountSelector } from '@/components/dashboard';
import { PositionFilters, PositionTable } from '@/components/positions';
import { useAccounts, useAccountSummary, useAllPositions, usePositionFilters, DEFAULT_PAGE_SIZE } from '@/hooks';
import { applyFilters } from '@/utils/positionFilters';
import { formatDateTime } from '@/utils/formatters';

export function PositionsPage() {
  // Account selection state
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Data fetching hooks
  const { accounts, isLoading: accountsLoading } = useAccounts();
  const { summary } = useAccountSummary(selectedAccountId);
  const {
    positions,
    isLoading: positionsLoading,
    isRefreshing,
    refresh: refreshPositions,
    error: positionsError,
    truncated,
  } = useAllPositions(selectedAccountId);

  // Filter state from URL
  const { filters, setFilter, clearFilters, hasActiveFilters } = usePositionFilters();

  // Apply filters to ALL positions
  const filteredPositions = useMemo(
    () => applyFilters(positions, filters),
    [positions, filters]
  );

  // Client-side pagination over filtered results
  const pageSize = DEFAULT_PAGE_SIZE;
  const totalPages = Math.ceil(filteredPositions.length / pageSize);
  const paginatedPositions = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredPositions.slice(start, start + pageSize);
  }, [filteredPositions, currentPage, pageSize]);

  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  // Reset to page 1 when filters change
  const handleFilterChange = useCallback(
    <K extends keyof typeof filters>(key: K, value: (typeof filters)[K]) => {
      setCurrentPage(1);
      setFilter(key, value);
    },
    [setFilter]
  );

  const handleClearFilters = useCallback(() => {
    setCurrentPage(1);
    clearFilters();
  }, [clearFilters]);

  // Handle account selection
  const handleAccountSelect = useCallback((accountId: string) => {
    setSelectedAccountId(accountId);
    setCurrentPage(1);
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refreshPositions();
  }, [refreshPositions]);

  // Auto-select first account if none selected
  if (!selectedAccountId && accounts.length > 0 && !accountsLoading) {
    const firstAccount = accounts[0];
    if (firstAccount) {
      setSelectedAccountId(firstAccount.account_id);
    }
  }

  const isLoading = accountsLoading || (selectedAccountId && positionsLoading);
  const lastUpdated = new Date().toISOString();

  // Range display
  const rangeStart = filteredPositions.length > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(currentPage * pageSize, filteredPositions.length);

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header with account selector and refresh */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-dark-100">Positions</h1>
            <AccountSelector
              accounts={accounts}
              selectedAccountId={selectedAccountId}
              onSelect={handleAccountSelect}
              isLoading={accountsLoading}
            />
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-dark-400">
              Last updated: {formatDateTime(lastUpdated)}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={!selectedAccountId || isRefreshing}
            >
              {isRefreshing ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Refreshing...
                </>
              ) : (
                'Refresh'
              )}
            </Button>
          </div>
        </div>

        {/* Filters */}
        {selectedAccountId && (
          <PositionFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClear={handleClearFilters}
            hasActiveFilters={hasActiveFilters}
          />
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        )}

        {/* No account selected */}
        {!isLoading && !selectedAccountId && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-dark-400">Select an account to view positions</p>
            </CardContent>
          </Card>
        )}

        {/* Positions content */}
        {!isLoading && selectedAccountId && (
          <Card>
            {truncated && (
              <div className="border-b border-dark-700 bg-amber-500/5 px-4 py-2.5">
                <p className="text-sm text-amber-400">
                  Showing the most recent 1,000 positions. Use filters to narrow results.
                </p>
              </div>
            )}
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>
                Positions ({rangeStart}–{rangeEnd} of {filteredPositions.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <PositionTable
                positions={paginatedPositions}
                netLiquidity={summary?.total_cost_basis}
                isLoading={positionsLoading}
                error={positionsError}
              />
              {filteredPositions.length > pageSize && (
                <Pagination
                  currentPage={currentPage}
                  hasNextPage={hasNextPage}
                  hasPrevPage={hasPrevPage}
                  onNextPage={() => setCurrentPage((p) => p + 1)}
                  onPrevPage={() => setCurrentPage((p) => p - 1)}
                  onFirstPage={() => setCurrentPage(1)}
                  pageItemCount={paginatedPositions.length}
                  pageSize={pageSize}
                />
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
}
